// useToast hook - Toast notification sistemi
// Success, error, warning, info mesajlari gosterir
import { createContext, ReactNode, useCallback, useContext, useState } from 'react'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  message: string
  duration: number
}

export interface UseToastReturn {
  // Toast state
  toasts: Toast[]

  // Toast actions
  success: (message: string, duration?: number) => void
  error: (message: string, duration?: number) => void
  warning: (message: string, duration?: number) => void
  info: (message: string, duration?: number) => void
  dismiss: (id: string) => void
  dismissAll: () => void
}

// Default toast suresi (ms)
const DEFAULT_DURATION = 3000

// Toast Context
const ToastContext = createContext<UseToastReturn | undefined>(undefined)

/**
 * ToastProvider Component
 *
 * App.tsx'te tum uygulamayi wrap et
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  // Toast ekleme (generic)
  const addToast = useCallback((type: ToastType, message: string, duration = DEFAULT_DURATION) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    const toast: Toast = {
      id,
      type,
      message,
      duration,
    }

    setToasts(prev => [...prev, toast])

    // Otomatik dismiss
    if (duration > 0) {
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id))
      }, duration)
    }

    return id
  }, [])

  // Success toast
  const success = useCallback((message: string, duration?: number) => {
    addToast('success', message, duration)
  }, [addToast])

  // Error toast
  const error = useCallback((message: string, duration?: number) => {
    addToast('error', message, duration)
  }, [addToast])

  // Warning toast
  const warning = useCallback((message: string, duration?: number) => {
    addToast('warning', message, duration)
  }, [addToast])

  // Info toast
  const info = useCallback((message: string, duration?: number) => {
    addToast('info', message, duration)
  }, [addToast])

  // Manuel dismiss
  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Tum toast'lari dismiss et
  const dismissAll = useCallback(() => {
    setToasts([])
  }, [])

  const value: UseToastReturn = {
    toasts,
    success,
    error,
    warning,
    info,
    dismiss,
    dismissAll,
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  )
}

/**
 * useToast Hook
 *
 * Toast notification sistemi
 * Success, error, warning, info mesajlari gosterir
 * Otomatik dismiss (duration sonrasi)
 *
 * @example
 * ```tsx
 * function ProjectForm() {
 *   const { success, error } = useToast()
 *
 *   async function handleSubmit(data) {
 *     try {
 *       await projectService.create(data)
 *       success('Proje basariyla olusturuldu!')
 *     } catch (err) {
 *       error('Proje olusturulamadi: ' + err.message)
 *     }
 *   }
 *
 *   return <form onSubmit={handleSubmit}>...</form>
 * }
 * ```
 */
export function useToast(): UseToastReturn {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}
